"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import Starfield from "./Starfield";

const NAV = [
  { label: "Start", href: "/#top" },
  { label: "Server", href: "/#server" },
  { label: "Community", href: "/#community" },
  { label: "Über uns", href: "/#about" },
];

const LEGAL = [
  { label: "Impressum", href: "/impressum" },
  { label: "Datenschutz", href: "/datenschutz" },
  { label: "Cookie-Hinweise", href: "/cookie-einstellungen" },
];

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("/#top");
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // While the full-screen menu is open it behaves as a modal dialog: the page
  // behind it is made inert (removed from the tab order + the accessibility
  // tree), focus is moved into the menu and trapped there, Escape closes it,
  // and focus returns to the toggle button on close (WCAG 2.4.3).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const header = headerRef.current;
    const dialog = dialogRef.current;

    // Inert everything behind the menu except the header (which keeps the close
    // button + wordmark reachable) and the menu overlay itself.
    const inerted: Element[] = [];
    for (const el of Array.from(document.body.children)) {
      if (el === header || el === dialog || el.hasAttribute("inert")) continue;
      el.setAttribute("inert", "");
      inerted.push(el);
    }

    // Focusable elements, in DOM order, across the header + the menu.
    const focusable = () => {
      const els: HTMLElement[] = [];
      if (header) els.push(...header.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (dialog) els.push(...dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      return els;
    };

    // Move focus into the menu (its first link).
    dialog?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (!current || current === first || !items.includes(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!current || current === last || !items.includes(current))) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      for (const el of inerted) el.removeAttribute("inert");
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      <header ref={headerRef} className="fixed inset-x-0 top-0 z-[70]">
        {/* Scrim keeps the wordmark legible over light content underneath. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-surface-base/80 via-surface-base/40 to-transparent" />
        <div className="relative mx-auto flex max-w-page items-center justify-between px-6 py-5 md:px-[100px]">
          <Link
            href="/"
            aria-label="Infinity City — Start"
            onClick={() => {
              setActive("/#top");
              setOpen(false);
            }}
            className="flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
          >
            <Image
              src="/logo-infinity-symbol.webp"
              alt=""
              width={55}
              height={30}
              className="h-7 w-auto drop-shadow-[0_2px_10px_rgba(168,85,247,0.5)]"
              priority
            />
            <span className="font-display text-lg font-bold tracking-tight [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">
              Infinity City
            </span>
          </Link>

          <button
            ref={triggerRef}
            type="button"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            aria-controls="site-menu"
            onClick={() => setOpen((v) => !v)}
            className="relative grid h-11 w-11 place-items-center rounded-xl outline-none transition-opacity hover:opacity-70 focus-visible:ring-2 focus-visible:ring-purple-400/70"
          >
            <span className="relative block h-3.5 w-5">
              <span
                className={`absolute left-0 top-0 h-0.5 w-5 rounded-full bg-white transition-all duration-300 ${
                  open ? "top-1.5 rotate-45" : ""
                }`}
              />
              <span
                className={`absolute bottom-0 left-0 h-0.5 w-5 rounded-full bg-white transition-all duration-300 ${
                  open ? "bottom-1.5 -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Full-screen menu overlay — light CSS fade only (no scroll/JS animation).
          Acts as a modal dialog while open; `inert` when closed keeps its links
          out of the tab order + accessibility tree. */}
      <div
        ref={dialogRef}
        id="site-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Hauptmenü"
        inert={!open}
        className={`fixed inset-0 z-[60] overflow-hidden transition-opacity duration-200 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-surface-base" />
        <div className="absolute left-1/2 top-1/2 h-[120vh] w-[120vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(126,34,206,0.45)_0%,rgba(10,10,11,0)_60%)]" />
        <Starfield />

        <nav className="relative flex h-full flex-col items-center justify-center gap-3">
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => {
                setActive(item.href);
                setOpen(false);
              }}
              className={`font-display text-4xl font-bold tracking-tight transition-colors md:text-5xl ${
                active === item.href
                  ? "text-purple-400"
                  : "text-white hover:text-purple-300"
              }`}
            >
              {item.label}
            </Link>
          ))}

          <div className="absolute bottom-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-6 text-sm text-gray-400">
            {LEGAL.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="transition-colors hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}
